import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSnackbar } from '../../../shared/hooks/useSnackbar';
import { useEventManagementService } from '../services/eventManagementService';
import { useCustomerService } from '../../customer/services/customerService';
import { useInvestigationService } from '../../investigation/services/investigationService';
import type { RuleEditorMode } from '../components/RulesTabContent';
import type { ActionLogEntry, Customer, Investigation } from '../../../types/backend';
import type {
  EventRuleListItemExtended,
  EventRuleExtended,
  RuleTemplate,
  RuleTemplateListItem,
  LiveEvent,
} from '../../../types/frontend';

const ACTION_LOG_TAB = 3;

interface UseEventManagementOptions {
  customerId: string;
  isOpen: boolean;
  onClose: () => void;
  onInvestigationCreated?: (investigation: Investigation, initialMessage?: string) => void;
  initialOpsItemId?: string | null;
}

export function useEventManagement({
  customerId,
  isOpen,
  onClose,
  onInvestigationCreated,
  initialOpsItemId,
}: UseEventManagementOptions) {
  const eventService = useEventManagementService();
  const customerService = useCustomerService();
  const investigationService = useInvestigationService();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [rules, setRules] = useState<EventRuleListItemExtended[]>([]);
  const [eventCount, setEventCount] = useState(0);
  const [actionLogCount, setActionLogCount] = useState(0);
  const [templates, setTemplates] = useState<RuleTemplateListItem[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ruleMode, setRuleMode] = useState<RuleEditorMode>({ kind: 'none' });
  const [loadingRule, setLoadingRule] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<RuleTemplate | null>(null);
  const [selectedTemplateItem, setSelectedTemplateItem] = useState<RuleTemplateListItem | null>(null);
  const [isNewTemplate, setIsNewTemplate] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'rule'; id: string } | { type: 'template'; id: string; owner: string } | null>(null);
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const loadData = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setError(null);
    try {
      const [customerData, rulesData, templatesData] = await Promise.all([
        customerService.getCustomerById(customerId),
        eventService.getEventRules(customerId),
        eventService.getTemplates(customerId),
      ]);
      setCustomer(customerData);
      setRules(rulesData);
      setTemplates(templatesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [customerId, customerService, eventService]);

  useEffect(() => {
    if (isOpen) {
      loadData();
      setActiveTab(initialOpsItemId ? ACTION_LOG_TAB : 0);
      setRuleMode({ kind: 'none' });
      setSelectedTemplate(null);
      setIsNewTemplate(false);
    }
  }, [isOpen, loadData, initialOpsItemId]);

  const handleSelectRule = useCallback(async (ruleItem: EventRuleListItemExtended) => {
    if (!customerId) return;
    setLoadingRule(true);
    setRuleMode({ kind: 'none' });
    try {
      const rule = await eventService.getEventRule(customerId, ruleItem.rule_id);
      setRuleMode({ kind: 'editing', ruleItem, rule });
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to load rule', 'error');
    } finally {
      setLoadingRule(false);
    }
  }, [customerId, eventService, showSnackbar]);

  const handleNewRule = useCallback(() => setRuleMode({ kind: 'new' }), []);

  const handleRuleSaved = useCallback(async (savedRule: EventRuleExtended) => {
    const isEdit = ruleMode.kind === 'editing';
    showSnackbar(isEdit ? 'Rule updated successfully' : 'Rule created successfully');
    const refreshedRules = await eventService.getEventRules(customerId);
    setRules(refreshedRules);
    const ruleItem = refreshedRules.find(r => r.rule_id === savedRule.rule_id);
    if (ruleItem) {
      setRuleMode({ kind: 'editing', ruleItem, rule: savedRule });
    } else {
      setRuleMode({ kind: 'none' });
    }
  }, [ruleMode.kind, customerId, eventService, showSnackbar]);

  const handleDeleteRuleConfirm = useCallback(() => {
    if (ruleMode.kind === 'editing') {
      setDeleteTarget({ type: 'rule', id: ruleMode.rule.rule_id });
    }
  }, [ruleMode]);

  const executeDeleteRule = useCallback(async () => {
    if (!customerId || !deleteTarget || deleteTarget.type !== 'rule') return;
    const ruleId = deleteTarget.id;
    setDeleteTarget(null);
    try {
      await eventService.deleteEventRule(customerId, ruleId);
      showSnackbar('Rule deleted successfully');
      setRuleMode({ kind: 'none' });
      await loadData();
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to delete rule', 'error');
    }
  }, [customerId, deleteTarget, eventService, loadData, showSnackbar]);

  const handleDiscardRule = useCallback(() => setRuleMode({ kind: 'none' }), []);

  const handleSelectTemplate = useCallback(async (t: RuleTemplateListItem) => {
    setSelectedTemplateItem(t);
    setLoadingTemplate(true);
    setSelectedTemplate(null);
    try {
      const full = await eventService.getTemplate(t.template_id, t.owner, t.customer_id || customerId);
      setSelectedTemplate(full);
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to load template', 'error');
    } finally {
      setLoadingTemplate(false);
    }
  }, [eventService, showSnackbar, customerId]);

  const handleNewTemplate = useCallback(() => {
    setSelectedTemplate(null);
    setSelectedTemplateItem(null);
    setIsNewTemplate(true);
  }, []);

  const handleTemplateSaved = useCallback(async (savedTemplate: RuleTemplate) => {
    showSnackbar(isNewTemplate ? 'Template created' : 'Template updated');
    setIsNewTemplate(false);
    setSelectedTemplate(savedTemplate);
    try {
      const refreshedTemplates = await eventService.getTemplates(customerId);
      setTemplates(refreshedTemplates);
      setSelectedTemplateItem(refreshedTemplates.find(t => t.template_id === savedTemplate.template_id) ?? null);
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to refresh templates', 'error');
    }
  }, [customerId, eventService, isNewTemplate, showSnackbar]);

  const handleDeleteTemplateConfirm = useCallback(() => {
    if (selectedTemplate) {
      setDeleteTarget({ type: 'template', id: selectedTemplate.template_id, owner: selectedTemplate.owner });
    }
  }, [selectedTemplate]);

  const executeDeleteTemplate = useCallback(async () => {
    if (!deleteTarget || deleteTarget.type !== 'template') return;
    const { id, owner } = deleteTarget;
    setDeleteTarget(null);
    try {
      await eventService.deleteTemplate(id, owner as 'global' | 'customer', customerId);
      showSnackbar('Template deleted');
      setSelectedTemplate(null);
      setSelectedTemplateItem(null);
      setIsNewTemplate(false);
      await loadData();
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to delete template', 'error');
    }
  }, [customerId, deleteTarget, eventService, loadData, showSnackbar]);

  const handleDiscardTemplate = useCallback(() => {
    setSelectedTemplate(null);
    setSelectedTemplateItem(null);
    setIsNewTemplate(false);
  }, []);

  const handleUseTemplateAsRule = useCallback(() => {
    if (!selectedTemplate) return;
    setRuleMode({ kind: 'from-template', template: selectedTemplate });
    setActiveTab(0);
  }, [selectedTemplate]);

  const handleCreateFromEvent = useCallback((event: LiveEvent) => {
    setRuleMode({ kind: 'from-event', event });
    setActiveTab(0);
  }, []);

  const handleStartInvestigation = useCallback(async (event: LiveEvent) => {
    if (!customerId) return;
    try {
      const title = `[${event.source}] ${event.detail_type}`;
      const initialMessage = [
        `Investigate this event from our recent events:`,
        ``,
        `**Event Details:**`,
        `- **Source**: ${event.source}`,
        `- **Detail Type**: ${event.detail_type}`,
        `- **Account**: ${event.account}`,
        `- **Region**: ${event.region}`,
        `- **Timestamp**: ${event.timestamp}`,
        `- **Event ID**: ${event.id}`,
        ``,
        `**Event Payload:**`,
        '```json',
        JSON.stringify(event.payload, null, 2),
        '```',
        ``,
        `Please analyze this event and help me understand:`,
        `1. What happened and what triggered this event?`,
        `2. Is this event expected or does it indicate an issue?`,
        `3. What actions, if any, should be taken?`,
      ].join('\n');

      const investigation = await investigationService.createInvestigation(customerId, {
        title,
        scope: [],
      });

      onClose();
      onInvestigationCreated?.(investigation, initialMessage);
      showSnackbar(`Investigation created: ${title}`);
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to create investigation', 'error');
    }
  }, [customerId, investigationService, onClose, onInvestigationCreated, showSnackbar]);

  const handleStartInvestigationFromActionLog = useCallback(async (entry: ActionLogEntry) => {
    if (!customerId) return;
    try {
      const title = `[Action Log] ${entry.title}`;
      const lines = [
        `Take over action log item ${entry.opsitem_id} and continue the investigation.`,
        ``,
        `**Action log item:**`,
        `- **Title**: ${entry.title}`,
        `- **Reference**: ${entry.opsitem_id}`,
        `- **Severity**: ${entry.severity}`,
        `- **Status**: ${entry.status}`,
      ];
      if (entry.rule_name) lines.push(`- **Rule**: ${entry.rule_name}`);
      if (entry.source) lines.push(`- **Source**: ${entry.source}`);
      if (entry.detail_type) lines.push(`- **Detail type**: ${entry.detail_type}`);
      lines.push(`- **Created**: ${entry.created_at}`);

      if (entry.automation_result?.result_summary) {
        lines.push(
          ``,
          `**Automation already ran** (run ${entry.automation_result.run_id ?? 'n/a'}, status ${entry.automation_result.status ?? 'n/a'}):`,
          '```',
          entry.automation_result.result_summary,
          '```',
        );
      }

      lines.push(
        ``,
        `Load the action log item by its id to pull its full state, pick up where the automation left off, and tell me:`,
        `1. What is the current state of this action log item?`,
        `2. What did the automation find or do, and what is still open?`,
        `3. What actions should be taken to resolve it?`,
      );
      const initialMessage = lines.join('\n');

      const investigation = await investigationService.createInvestigation(customerId, {
        title,
        scope: [],
      });

      onClose();
      onInvestigationCreated?.(investigation, initialMessage);
      showSnackbar(`Investigation created: ${title}`);
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to create investigation', 'error');
    }
  }, [customerId, investigationService, onClose, onInvestigationCreated, showSnackbar]);

  const ruleEditorKey = useMemo(() => {
    switch (ruleMode.kind) {
      case 'editing': return ruleMode.rule.rule_id;
      case 'from-template': return `template-${ruleMode.template.template_id}`;
      case 'from-event': return `event-${ruleMode.event.id}`;
      case 'new': return 'new';
      default: return 'none';
    }
  }, [ruleMode]);

  const footerCount = activeTab === 0 ? rules.length
    : activeTab === 1 ? templates.length
    : activeTab === 2 ? eventCount
    : actionLogCount;
  const footerLabel = activeTab === 0
    ? (rules.length === 1 ? 'rule' : 'rules')
    : activeTab === 1
      ? (templates.length === 1 ? 'template' : 'templates')
      : activeTab === 2
        ? (eventCount === 1 ? 'event' : 'events')
        : (actionLogCount === 1 ? 'entry' : 'entries');

  return {
    customer,
    rules,
    templates,
    activeTab,
    setActiveTab,
    loading,
    error,
    ruleMode,
    ruleEditorKey,
    loadingRule,
    selectedTemplate,
    selectedTemplateItem,
    isNewTemplate,
    loadingTemplate,
    deleteTarget,
    footerCount,
    footerLabel,
    snackbar,
    closeSnackbar,
    setEventCount,
    setActionLogCount,
    setError,
    setDeleteTarget,
    handleSelectRule,
    handleNewRule,
    handleRuleSaved,
    handleDeleteRuleConfirm,
    executeDeleteRule,
    handleDiscardRule,
    handleSelectTemplate,
    handleNewTemplate,
    handleTemplateSaved,
    handleDeleteTemplateConfirm,
    executeDeleteTemplate,
    handleDiscardTemplate,
    handleUseTemplateAsRule,
    handleCreateFromEvent,
    handleStartInvestigation,
    handleStartInvestigationFromActionLog,
  };
}
